import { TableChip } from '../../components/PremiumDesign';
import { normalizeTags } from '../../utils/tags';
import { ResponsiveBadgeList } from '../../components/ResponsiveBadgeList';

function renderTagChip(tag, maxTagWidth) {
  return (
    <div
      key={tag.id ?? tag.name}
      className="relative group/chip inline-flex shrink-0 flex-shrink-0 hover:z-30"
      style={{ flexShrink: 0 }}
    >
      <TableChip
        tone="primary"
        className={`${maxTagWidth} min-w-0 !h-[24px] sm:!h-[28px] !px-2 sm:!px-2.5 !text-[11px] sm:!text-xs cursor-pointer whitespace-nowrap shrink-0`}
        style={
          tag.color
            ? {
                backgroundColor: `${tag.color}15`,
                borderColor: `${tag.color}40`,
                color: tag.color,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }
            : { whiteSpace: 'nowrap', flexShrink: 0 }
        }
      >
        <span className="truncate block min-w-0 max-w-full whitespace-nowrap">
          {tag.name}
        </span>
      </TableChip>

      {/* Instant hover tooltip showing full text */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/chip:flex flex-col items-center z-[100] pointer-events-none transition-all duration-150">
        <div className="bg-[#1F2241] text-white text-[11px] sm:text-xs font-medium py-1 px-2.5 rounded shadow-xl whitespace-nowrap max-w-[260px] truncate">
          {tag.name}
        </div>
        <div className="w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#1F2241]" />
      </div>
    </div>
  );
}

function renderMoreChip(extra, hiddenTags) {
  const hiddenNames = (hiddenTags || [])
    .map((tag) => (typeof tag === 'object' && tag !== null ? tag.name : tag))
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className="relative group/chip inline-flex shrink-0 flex-shrink-0 hover:z-30"
      style={{ flexShrink: 0 }}
    >
      <TableChip
        tone="neutral"
        className="!h-[24px] sm:!h-[28px] !px-2 sm:!px-2.5 !text-[11px] sm:!text-xs cursor-pointer whitespace-nowrap shrink-0 font-medium"
        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        +{extra}
      </TableChip>

      {/* Instant hover tooltip for extra tags */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/chip:flex flex-col items-center z-[100] pointer-events-none transition-all duration-150">
        <div className="bg-[#1F2241] text-white text-[11px] sm:text-xs font-medium py-1 px-2.5 rounded shadow-xl whitespace-normal max-w-[260px] text-center">
          {hiddenNames}
        </div>
        <div className="w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#1F2241]" />
      </div>
    </div>
  );
}

export function TagChips({
  tags,
  max,
  className = '',
  hideEmpty = false,
  wrap = false,
  maxTagWidth = 'max-w-[120px] sm:max-w-[160px] md:max-w-[200px]',
  responsive = true,
}) {
  const items = normalizeTags(tags);
  if (items.length === 0) {
    if (hideEmpty) return null;
    return <span className="text-text-muted text-xs sm:text-sm">-</span>;
  }

  if (wrap || !responsive) {
    const limit = max ?? 2;
    const visible = items.slice(0, limit);
    const extra = items.length - visible.length;

    return (
      <div
        className={`flex items-center ${wrap ? 'flex-wrap' : 'flex-nowrap'} gap-1.5 min-w-0 max-w-full whitespace-nowrap ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: wrap ? 'wrap' : 'nowrap',
          whiteSpace: 'nowrap',
        }}
      >
        {visible.map((tag) => renderTagChip(tag, maxTagWidth))}
        {extra > 0 && renderMoreChip(extra, items.slice(limit))}
      </div>
    );
  }

  return (
    <ResponsiveBadgeList
      items={items}
      max={max}
      gap={6}
      className={`gap-1.5 min-w-0 max-w-full whitespace-nowrap ${className}`}
      renderItem={(tag) => renderTagChip(tag, maxTagWidth)}
      renderMore={(extra, hiddenTags) => renderMoreChip(extra, hiddenTags)}
    />
  );
}
